/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { projectDailySales } from './projection';
import { KpiCard, DateStepper, ChartCard, StatusPill, SegmentedPills, rangeForPeriod, priorPeriodRange } from './components/systemKit';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];
// Role palette is a DATA COLOR (see DESIGN_SYSTEM.md §Data Colors). Stays as
// Tailwind classnames since .dark overrides already handle these.
const ROLE_COLORS = { 'Cook': 'bg-orange-100 text-orange-700', 'Prep Cook': 'bg-red-100 text-red-600', 'Dishwasher': 'bg-slate-100 text-slate-600', 'Server': 'bg-blue-100 text-blue-700', 'Bartender': 'bg-amber-100 text-amber-700', 'Host': 'bg-purple-100 text-purple-700', 'Busser': 'bg-teal-100 text-teal-700', 'Manager': 'bg-gray-100 text-gray-700' };
const PERIOD_DEFAULTS = { 'Breakfast': { start: '06:00', end: '11:00' }, 'Lunch': { start: '11:00', end: '16:00' }, 'Dinner': { start: '16:00', end: '23:00' }, 'Late Night': { start: '22:00', end: '02:00' } };
// Meal period band styling (grid context) — see DESIGN_SYSTEM.md §Meal period sections.
const PERIOD_BAND = {
  'Breakfast':  { bg: 'var(--meal-band-bg-breakfast)',  text: 'var(--meal-band-text-breakfast)' },
  'Lunch':      { bg: 'var(--meal-band-bg-lunch)',      text: 'var(--meal-band-text-lunch)' },
  'Dinner':     { bg: 'var(--meal-band-bg-dinner)',     text: 'var(--meal-band-text-dinner)' },
  'Late Night': { bg: 'var(--meal-band-bg-latenight)',  text: 'var(--meal-band-text-latenight)' },
};

function getWeekDates(offset = 0) {
  const now = new Date(); const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(now); mon.setDate(diff + offset * 7); mon.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
}
function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function calcHours(start, end) { if (!start || !end) return 7; const [sh, sm] = start.split(':').map(Number); const [eh, em] = end.split(':').map(Number); let h = (eh * 60 + em - sh * 60 - sm) / 60; if (h < 0) h += 24; return Math.round(h * 10) / 10; }
function sanitizeShift(s) { const pd = PERIOD_DEFAULTS[s.shift_name]; if (pd && s.start_time) { const [sh] = s.start_time.split(':').map(Number); const [psh] = pd.start.split(':').map(Number); const [peh] = pd.end.split(':').map(Number); if (peh > psh && (sh >= peh || sh < psh - 1)) return { ...s, start_time: pd.start }; } return s; }
function fmtTime(t) { if (!t) return '?'; const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; }

// Target-based tone derivation — under target-3 → success, within ±3 → warning, over → critical.
// Used for KpiCard valueTone + the trend table column.
function pctTone(p) {
  if (p == null) return 'neutral';
  if (p <= 25) return 'success';
  if (p <= 28) return 'warning';
  return 'critical';
}
function primeTone(p) {
  if (p == null) return 'neutral';
  if (p <= 60) return 'success';
  if (p <= 65) return 'warning';
  return 'critical';
}

// Bar chart color mapping — data color (keeps the at-a-glance semantic encoding).
function barFill(laborPct) {
  if (laborPct == null) return '#94A3B8';
  if (laborPct <= 25) return '#22C55E';
  if (laborPct <= 28) return '#F59E0B';
  return '#EF4444';
}

export default function LaborCosts({ companyId }) {
  const [tab, setTab] = useState('week');
  // Period-aware date control at the top — drives summary KPIs + period
  // totals. Schedule grid below keeps weekOffset for daily breakdown
  // (Option A from the period unification plan — per-shift detail is a
  // weekly workflow). See DESIGN_SYSTEM.md §Period-aware DateStepper.
  const [period, setPeriod] = useState('week');
  const [range, setRange] = useState(() => rangeForPeriod('week', new Date()));
  const [weekOffset, setWeekOffset] = useState(0);
  const [salesData, setSalesData] = useState({});
  const [shifts, setShifts] = useState([]);
  const [prevShifts, setPrevShifts] = useState([]);
  const [empMap, setEmpMap] = useState({});
  // foodEntries was previously used for the weekly Prime Cost KPI. Now
  // computed at period scope via periodFoodTotal — this state remains
  // wired so the existing fetch keeps working and downstream features can
  // hook in if needed.
  // eslint-disable-next-line no-unused-vars
  const [foodEntries, setFoodEntries] = useState({});
  const [drillDown, setDrillDown] = useState(null);
  const [drillShifts, setDrillShifts] = useState([]);
  const [trendRange, setTrendRange] = useState(8);
  const [trendData, setTrendData] = useState([]);
  // Period-range aggregates for the summary KPIs above the schedule grid.
  const [periodShifts, setPeriodShifts] = useState([]);
  const [periodSalesTotal, setPeriodSalesTotal] = useState(0);
  const [periodFoodTotal, setPeriodFoodTotal] = useState(0);
  const [periodPriorLabor, setPeriodPriorLabor] = useState(0);

  const weekDates = getWeekDates(weekOffset);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  useEffect(() => { if (companyId) fetchData(); }, [companyId, weekOffset, range.start, range.end]);
  useEffect(() => { if (companyId && tab === 'trends') fetchTrends(); }, [companyId, tab, trendRange]);

  async function fetchData() {
    const ds = weekDates.map(fmtDate);
    const prevDs = getWeekDates(weekOffset - 1).map(fmtDate);
    // Period-range fetches power the summary KPIs above the schedule grid.
    const prior = priorPeriodRange(period, range);
    const [salesRes, shiftRes, prevShiftRes, empRes, foodRes,
           periodShiftsRes, periodSalesRes, periodFoodRes, priorShiftsRes] = await Promise.all([
      supabase.from('labor_sales').select('*').eq('company_id', companyId).in('date', ds),
      supabase.from('shifts').select('*').eq('company_id', companyId).in('day', ds),
      supabase.from('shifts').select('*').eq('company_id', companyId).in('day', prevDs),
      supabase.from('employees').select('id, name, hourly_rate, role').eq('company_id', companyId),
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).in('date', ds),
      supabase.from('shifts').select('day, start_time, end_time, employee_id').eq('company_id', companyId).gte('day', range.start).lte('day', range.end),
      supabase.from('labor_sales').select('total_sales').eq('company_id', companyId).gte('date', range.start).lte('date', range.end),
      supabase.from('food_cost_entries').select('total').eq('company_id', companyId).gte('date', range.start).lte('date', range.end),
      supabase.from('shifts').select('day, start_time, end_time, employee_id').eq('company_id', companyId).gte('day', prior.start).lte('day', prior.end),
    ]);
    const sm = {}; (salesRes.data || []).forEach(s => { sm[s.date] = s; }); setSalesData(sm);
    setShifts(shiftRes.data || []);
    setPrevShifts(prevShiftRes.data || []);
    const em = {}; (empRes.data || []).forEach(e => { em[String(e.id)] = e; }); setEmpMap(em);
    const fm = {}; (foodRes.data || []).forEach(e => { fm[e.date] = e; }); setFoodEntries(fm);

    setPeriodShifts(periodShiftsRes.data || []);
    setPeriodSalesTotal((periodSalesRes.data || []).reduce((s, r) => s + Number(r.total_sales || 0), 0));
    setPeriodFoodTotal((periodFoodRes.data || []).reduce((s, r) => s + Number(r.total || 0), 0));
    // Prior-period labor cost (calendar-aligned via priorPeriodRange).
    // Computed inline since employee rate map is now in scope.
    const priorEmpRates = {};
    (empRes.data || []).forEach(e => { priorEmpRates[String(e.id)] = e.hourly_rate || 0; });
    const priorLabor = (priorShiftsRes.data || []).map(sanitizeShift).reduce(
      (a, sh) => a + calcHours(sh.start_time, sh.end_time) * (priorEmpRates[String(sh.employee_id)] || 0), 0
    );
    setPeriodPriorLabor(priorLabor);
  }

  async function fetchTrends() {
    const weeks = [];
    for (let i = 0; i < trendRange; i++) {
      const wd = getWeekDates(-i);
      weeks.push({ start: fmtDate(wd[0]), end: fmtDate(wd[6]), dates: wd.map(fmtDate), label: `${wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` });
    }
    const start = weeks[weeks.length - 1].start;
    const end = weeks[0].end;
    const histStartDate = new Date(start + 'T00:00:00');
    histStartDate.setDate(histStartDate.getDate() - 42);
    const histStartStr = fmtDate(histStartDate);
    const [sRes, shRes, eRes, fRes] = await Promise.all([
      supabase.from('labor_sales').select('date, total_sales, projected_sales').eq('company_id', companyId).gte('date', histStartStr).lte('date', end),
      supabase.from('shifts').select('day, start_time, end_time, employee_id').eq('company_id', companyId).gte('day', start).lte('day', end),
      supabase.from('employees').select('id, hourly_rate').eq('company_id', companyId),
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).gte('date', start).lte('date', end),
    ]);
    const allShifts = shRes.data || [];
    const allFood = fRes.data || [];
    const rates = {}; (eRes.data || []).forEach(e => { rates[String(e.id)] = e.hourly_rate || 0; });
    const salesByDate = {};
    const salesOverrides = {};
    (sRes.data || []).forEach(r => {
      salesByDate[r.date] = Number(r.total_sales || 0);
      if (r.projected_sales != null) salesOverrides[r.date] = Number(r.projected_sales);
    });
    const todayStr = fmtDate(new Date());
    const result = weeks.map(w => {
      const projMap = projectDailySales(w.dates, salesByDate, salesOverrides);
      const sales = w.dates.reduce((sum, ds) => {
        const actual = salesByDate[ds];
        if (actual != null && actual > 0) return sum + actual;
        if (ds < todayStr) return sum;
        return sum + (projMap[ds] || 0);
      }, 0);
      const labor = allShifts.filter(s => s.day >= w.start && s.day <= w.end).reduce((a, s) => a + calcHours(s.start_time, s.end_time) * (rates[String(s.employee_id)] || 0), 0);
      const food = allFood.filter(f => f.date >= w.start && f.date <= w.end).reduce((a, f) => a + (f.total || 0), 0);
      return { ...w, sales, labor, food, laborPct: sales > 0 ? (labor / sales) * 100 : null, foodPct: sales > 0 ? (food / sales) * 100 : null, primePct: sales > 0 ? ((labor + food) / sales) * 100 : null };
    }).reverse();
    setTrendData(result);
  }

  function getLaborCost(date) {
    const dateStr = fmtDate(date);
    return shifts.filter(s => s.day === dateStr).map(sanitizeShift).reduce((total, s) => total + calcHours(s.start_time, s.end_time) * (empMap[String(s.employee_id)]?.hourly_rate || 0), 0);
  }

  function openDrillDown(dayObj) {
    setDrillDown(dayObj);
    const dayShifts = shifts.filter(s => s.day === dayObj.dateStr).map(sanitizeShift).map(s => ({ ...s, emp: empMap[String(s.employee_id)] || null }));
    setDrillShifts(dayShifts);
  }

  const days = weekDates.map((date, i) => { const ds = fmtDate(date); const sale = salesData[ds]; return { day: DAYS[i], date, dateStr: ds, sales: sale?.total_sales || 0, labor: getLaborCost(date), entered: !!sale }; });
  const totalSales = days.filter(d => d.entered).reduce((a, b) => a + b.sales, 0);
  const totalLabor = days.reduce((a, b) => a + b.labor, 0);
  const laborPct = totalSales > 0 ? (totalLabor / totalSales) * 100 : null;
  // weekFoodTotal / primePct / vs-last-week deltas previously powered the
  // top-of-page KPIs. After the period unification commit, those KPIs are
  // computed over [range.start, range.end] from the period-scoped fetches.
  // The schedule grid still renders weekly labor totals via days[].labor and
  // laborPct above. prevShifts is kept for the inline week-context line.
  void prevShifts;

  // ── Period summary ───────────────────────────────────────────────────────
  // Labor / labor% / prime% computed across [range.start, range.end] from
  // the period-scoped fetches.
  const periodLaborTotal = periodShifts.map(sanitizeShift).reduce(
    (a, sh) => a + calcHours(sh.start_time, sh.end_time) * (empMap[String(sh.employee_id)]?.hourly_rate || 0), 0
  );
  const periodLaborPct = periodSalesTotal > 0 ? (periodLaborTotal / periodSalesTotal) * 100 : null;
  const periodPrimePct = periodSalesTotal > 0 ? ((periodLaborTotal + periodFoodTotal) / periodSalesTotal) * 100 : null;
  const periodVsPrior = periodPriorLabor > 0 ? periodLaborTotal - periodPriorLabor : null;
  const periodVsPriorPct = periodPriorLabor > 0 ? (periodVsPrior / periodPriorLabor) * 100 : null;
  const periodNoun = period === 'week' ? 'week' : period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : 'year';
  const periodPrevLabel = `vs Last ${periodNoun.charAt(0).toUpperCase() + periodNoun.slice(1)}`;

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Labor Costs</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Track labor spending vs sales</p>
          </div>
        </div>

        {/* Tabs — view switching on same dataset → segmented pills per DESIGN_SYSTEM.md §Tabs */}
        <div className="mb-6">
          <SegmentedPills
            value={tab}
            onChange={setTab}
            options={[
              { key: 'week', label: 'This Week' },
              { key: 'trends', label: 'Trends' },
            ]}
          />
        </div>

        {/* ═══ THIS PERIOD ═══ */}
        {tab === 'week' && (
          <div>
            {/* Period-aware DateStepper — canonical date control. Drives the
                summary KPIs below. The schedule grid further down keeps its
                own week-stepper. See DESIGN_SYSTEM.md §Period-aware DateStepper. */}
            <div className="flex items-center justify-end mb-6">
              <DateStepper
                period={period}
                onPeriodChange={setPeriod}
                range={range}
                onRangeChange={setRange}
                alignment="right"
              />
            </div>

            {/* Period summary cards — computed over [range.start, range.end]. */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <KpiCard
                label="Labor Cost"
                value={fmt$(periodLaborTotal)}
                secondary={`${periodShifts.length} shifts`}
              />
              <KpiCard
                label="Labor %"
                value={periodLaborPct != null ? `${periodLaborPct.toFixed(1)}%` : '\u2014'}
                valueTone={pctTone(periodLaborPct)}
                meaning={periodLaborPct != null ? pctTone(periodLaborPct) : undefined}
                secondary={periodLaborPct != null ? 'of sales' : 'Enter sales to see %'}
              />
              <KpiCard
                label={periodPrevLabel}
                value={periodVsPrior != null ? `${periodVsPrior <= 0 ? '\u2193' : '\u2191'} ${fmt$(Math.abs(periodVsPrior))}` : '\u2014'}
                valueTone={periodVsPrior != null ? (periodVsPrior <= 0 ? 'success' : 'critical') : 'neutral'}
                secondary={periodVsPriorPct != null ? `${periodVsPriorPct.toFixed(1)}% change` : `No data last ${periodNoun}`}
              />
              <KpiCard
                label="Prime Cost"
                value={periodPrimePct != null ? `${periodPrimePct.toFixed(1)}%` : '\u2014'}
                valueTone={primeTone(periodPrimePct)}
                secondary={periodFoodTotal > 0 ? 'food + labor' : 'Add food costs'}
              />
            </div>

            {/* ── Schedule grid (week-only) ───────────────────────────────
                Per-shift detail is fundamentally a weekly workflow. Stays
                here as its own section regardless of selected period. The
                week-stepper navigates which week to inspect, independent of
                the summary period above. */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Schedule by day
                <span className="text-xs ml-2" style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {`Daily breakdown for ${weekLabel}`}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  This week: <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt$(totalLabor)}</span>
                  {laborPct != null && <span className="ml-2" style={{ color: 'var(--text-muted)' }}>({laborPct.toFixed(1)}% of sales)</span>}
                </span>
                <DateStepper
                  label={weekLabel}
                  onPrev={() => setWeekOffset(w => w - 1)}
                  onNext={() => setWeekOffset(w => w + 1)}
                  onReset={weekOffset !== 0 ? () => setWeekOffset(0) : undefined}
                />
              </div>
            </div>

            {/* Daily table */}
            <div className="rounded-lg overflow-hidden mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 80px 90px 60px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                {['Day', 'Sales', 'Labor', '%', 'vs 25%', ''].map((h, hi) => (
                  <div key={h || hi} className="px-4 py-2.5" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', textAlign: hi >= 1 && hi <= 4 ? 'right' : 'left' }}>{h}</div>
                ))}
              </div>
              {days.map((d, i) => {
                const pct = d.entered && d.sales > 0 ? (d.labor / d.sales) * 100 : null;
                const diff = pct != null ? 25 - pct : null;
                const isToday = fmtDate(new Date()) === d.dateStr;
                return (
                  <div
                    key={i}
                    className="grid cursor-pointer transition-colors"
                    style={{
                      gridTemplateColumns: '1fr 1fr 1fr 80px 90px 60px',
                      borderBottom: i < days.length - 1 ? '1px solid var(--border)' : 'none',
                      background: isToday ? 'var(--accent-subtle)' : 'transparent',
                      minHeight: 52,
                    }}
                    onClick={() => openDrillDown(d)}
                    onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                    onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div className="px-4 py-3">
                      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {d.day} <span className="font-normal" style={{ color: 'var(--text-muted)' }}>{d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                      {d.entered ? fmt$(d.sales) : <span className="text-xs" style={{ color: 'var(--text-muted)' }}>tap to enter</span>}
                    </div>
                    <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                      {fmt$(d.labor)}
                    </div>
                    <div className="px-4 py-3" style={{ textAlign: 'right' }}>
                      {pct != null
                        ? <StatusPill tone={pctTone(pct)}>{pct.toFixed(1)}%</StatusPill>
                        : <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                    </div>
                    <div className="px-4 py-3 text-xs" style={{ textAlign: 'right' }}>
                      {diff != null
                        ? <span style={{ color: diff >= 0 ? 'var(--text-success)' : 'var(--text-critical)' }}>{diff >= 0 ? `${diff.toFixed(1)}% under` : `${Math.abs(diff).toFixed(1)}% over`}</span>
                        : ''}
                    </div>
                    <div className="px-4 py-3" />
                  </div>
                );
              })}
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 80px 90px 60px', background: 'var(--bg-subtle)', borderTop: '2px solid var(--border)' }}>
                <div className="px-4 py-3" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Week total</div>
                <div className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmt$(totalSales)}</div>
                <div className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmt$(totalLabor)}</div>
                <div className="px-4 py-3" style={{ textAlign: 'right' }}>{laborPct != null && <StatusPill tone={pctTone(laborPct)}>{laborPct.toFixed(1)}%</StatusPill>}</div>
                <div className="px-4 py-3 text-xs font-medium" style={{ textAlign: 'right' }}>
                  {laborPct != null && (
                    <span style={{ color: laborPct <= 25 ? 'var(--text-success)' : 'var(--text-critical)' }}>
                      {laborPct <= 25 ? 'on track' : 'over target'}
                    </span>
                  )}
                </div>
                <div />
              </div>
            </div>
          </div>
        )}

        {/* ═══ TRENDS ═══ */}
        {tab === 'trends' && (
          <div>
            <div className="mb-6">
              <SegmentedPills
                value={trendRange}
                onChange={setTrendRange}
                options={[
                  { key: 8,  label: '8 weeks' },
                  { key: 12, label: '12 weeks' },
                  { key: 26, label: '6 months' },
                  { key: 52, label: '12 months' },
                ]}
              />
            </div>

            {trendData.filter(w => w.labor > 0).length < 2 ? (
              <div className="rounded-lg p-12 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="text-3xl mb-3">{'\uD83D\uDCC8'}</div>
                <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Keep entering sales data</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Trends will appear after a few weeks</div>
              </div>
            ) : (
              <>
                {/* Bar chart */}
                <div className="mb-6">
                  <ChartCard
                    title="Weekly Labor Cost"
                    legend={
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><span className="w-4" style={{ borderTop: '2px dashed var(--border)' }} /> Target: 25%</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#22C55E' }} /> Under</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#F59E0B' }} /> Near</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#EF4444' }} /> Over</span>
                      </div>
                    }
                  >
                    <div className="flex items-end gap-1" style={{ height: 160 }}>
                      {trendData.map((w, i) => {
                        const maxLabor = Math.max(...trendData.map(t => t.labor));
                        const h = maxLabor > 0 ? (w.labor / maxLabor) * 140 : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${w.label}: ${fmt$(w.labor)}${w.laborPct != null ? ` (${w.laborPct.toFixed(1)}%)` : ''}`}>
                            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{w.labor > 0 ? `$${Math.round(w.labor / 1000)}k` : ''}</div>
                            <div style={{ height: Math.max(h, w.labor > 0 ? 4 : 0), width: '100%', maxWidth: 32, borderRadius: 4, background: barFill(w.laborPct), transition: 'height 0.3s ease' }} />
                            <div className="truncate w-full text-center" style={{ fontSize: 9, color: 'var(--text-muted)' }}>{w.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </ChartCard>
                </div>

                {/* Trend table */}
                <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 70px 70px 70px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    {['Week', 'Labor', 'Sales', 'Labor %', 'Food %', 'Prime %'].map((h, hi) => (
                      <div key={h} className="px-3 py-2" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', textAlign: hi === 0 ? 'left' : 'right' }}>{h}</div>
                    ))}
                  </div>
                  {trendData.filter(w => w.labor > 0 || w.sales > 0).reverse().map((w, i, arr) => {
                    const bestPct = Math.min(...arr.filter(x => x.laborPct != null).map(x => x.laborPct));
                    const worstPct = Math.max(...arr.filter(x => x.laborPct != null).map(x => x.laborPct));
                    const best = arr.length > 2 && w.laborPct != null && w.laborPct === bestPct;
                    const worst = arr.length > 2 && w.laborPct != null && w.laborPct === worstPct;
                    return (
                      <div
                        key={i}
                        className="grid"
                        style={{
                          gridTemplateColumns: '1fr 1fr 1fr 70px 70px 70px',
                          borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                          background: best ? 'var(--bg-success-subtle)' : worst ? 'var(--bg-critical-subtle)' : 'transparent',
                          minHeight: 44,
                        }}
                      >
                        <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{w.label}</div>
                        <div className="px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmt$(w.labor)}</div>
                        <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.sales > 0 ? fmt$(w.sales) : '\u2014'}</div>
                        <div className="px-3 py-2.5 text-xs font-semibold" style={{ color: w.laborPct != null ? ({ success: 'var(--text-success)', warning: 'var(--text-warning)', critical: 'var(--text-critical)', neutral: 'var(--text-muted)' })[pctTone(w.laborPct)] : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.laborPct != null ? `${w.laborPct.toFixed(1)}%` : '\u2014'}</div>
                        <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.foodPct != null ? `${w.foodPct.toFixed(1)}%` : '\u2014'}</div>
                        <div className="px-3 py-2.5 text-xs font-medium" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.primePct != null ? `${w.primePct.toFixed(1)}%` : '\u2014'}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Drill-down panel */}
      {drillDown && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 480, background: 'var(--bg-card)', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 50, display: 'flex', flexDirection: 'column' }}>
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
            <div>
              <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{drillDown.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{fmt$(drillDown.labor)} labor</span>
                {drillDown.entered && drillDown.sales > 0 && (
                  <StatusPill tone={pctTone((drillDown.labor / drillDown.sales) * 100)}>
                    {((drillDown.labor / drillDown.sales) * 100).toFixed(1)}%
                  </StatusPill>
                )}
              </div>
            </div>
            <button
              onClick={() => setDrillDown(null)}
              className="text-lg px-1 rounded-md transition-colors"
              style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
            >{'\u2715'}</button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Period breakdown. Each period header uses a meal-band grid-context pill per
                DESIGN_SYSTEM.md §Meal period sections (grid context). */}
            {PERIODS.filter(p => drillShifts.some(s => s.shift_name === p)).map(period => {
              const periodShifts = drillShifts.filter(s => s.shift_name === period);
              const periodCost = periodShifts.reduce((a, s) => a + calcHours(s.start_time, s.end_time) * (s.emp?.hourly_rate || 0), 0);
              const band = PERIOD_BAND[period];
              return (
                <div key={period} className="mb-4">
                  <div
                    className="flex items-center justify-between mb-2 px-3 py-1 rounded-md"
                    style={{ background: band.bg, borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: band.text }}>{period}</span>
                    <span className="text-xs font-semibold" style={{ color: band.text, fontVariantNumeric: 'tabular-nums' }}>{fmt$(periodCost)}</span>
                  </div>
                  {periodShifts.map(s => {
                    const hrs = calcHours(s.start_time, s.end_time);
                    const rate = s.emp?.hourly_rate || 0;
                    const cost = hrs * rate;
                    return (
                      <div
                        key={s.id}
                        className="flex items-center justify-between py-2"
                        style={{ borderBottom: '1px solid var(--border)' }}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-md ${ROLE_COLORS[s.role] || 'bg-gray-100 text-gray-600'}`}>{s.role}</span>
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{s.emp?.name || '?'}</span>
                        </div>
                        <div className="text-xs text-right" style={{ color: 'var(--text-secondary)' }}>
                          <div>{fmtTime(s.start_time)}{'\u2013'}{fmtTime(s.end_time)} ({hrs}h)</div>
                          <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{hrs}h {'\u00D7'} ${rate}/hr = {fmt$(cost)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {drillShifts.length === 0 && <div className="text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>No shifts scheduled</div>}
          </div>
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Enter sales on the Sales tab</span>
            <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Total: {fmt$(drillDown.labor)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
